import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { ApiError, asList } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { hrApi, type Employee } from '../lib/hrApi';
import { payrollApi, rupees, type StatutoryRule, type SalaryStructure, type PayrollRun,
  type PaymentFilePreview, type LopSettings } from '../lib/payrollApi';

/// Payroll (tenant-admin). The system ships with NO statutory rates — the school enters them and their
/// accountant verifies each one. A run computes as a draft, but CANNOT be finalized while any applied
/// rule is unverified; the backend refuses and names them.
export function PayrollPage() {
  const [tab, setTab] = useState<'runs' | 'rules' | 'structures'>('rules');
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>People</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Payroll</h1>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '3px solid var(--warn, #B7791F)' }}>
        <strong style={{ fontSize: 13 }}>⚠ Statutory rates are not supplied by Eduvora.</strong>
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4 }}>
          PF, ESI, professional tax and TDS rates change, vary by state, and depend on thresholds. Enter each rule below and have
          your accountant verify it. <strong>Payroll will refuse to finalize while any rule it uses is unverified</strong> — so a
          payslip can never be issued on a number nobody signed off.
        </p>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn active={tab === 'rules'} onClick={() => setTab('rules')}>Statutory rules</TabBtn>
        <TabBtn active={tab === 'structures'} onClick={() => setTab('structures')}>Salary structures</TabBtn>
        <TabBtn active={tab === 'runs'} onClick={() => setTab('runs')}>Runs</TabBtn>
      </div>

      {tab === 'rules' && <RulesTab />}
      {tab === 'structures' && <StructuresTab />}
      {tab === 'runs' && <RunsTab />}
    </div>
  );
}

function RulesTab() {
  const [rows, setRows] = useState<StatutoryRule[]>([]);
  const [f, setF] = useState({ kind: 'ProvidentFund', label: '', basis: 'PercentOfBase', value: '', floor: '0', ceiling: '0', employer: false, effectiveFrom: '', note: '' });
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => { payrollApi.listRules().then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!f.label.trim() || !f.effectiveFrom || !f.value) return;
    setError(null);
    try {
      await payrollApi.createRule({
        kind: f.kind, label: f.label.trim(), basis: f.basis, value: Number(f.value),
        wageFloorPaise: Number(f.floor), wageCeilingPaise: Number(f.ceiling),
        isEmployerContribution: f.employer, effectiveFrom: f.effectiveFrom, sourceNote: f.note.trim() || null,
      });
      setF({ ...f, label: '', value: '', note: '' }); load();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not create rule.'); }
  };

  const verify = async (r: StatutoryRule) => {
    const who = window.prompt(`Who is verifying "${r.label}"? (name of the accountant/CA confirming this rate is correct and current)`);
    if (!who?.trim()) return;
    const note = window.prompt('Source note (e.g. statute + date confirmed) — optional') ?? undefined;
    try { await payrollApi.verifyRule(r.id, who.trim(), note || undefined); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not verify.'); }
  };

  const unverified = rows.filter((r) => !r.isVerified).length;

  return (
    <div>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

      {rows.length === 0 && (
        <div className="card" style={{ padding: 24, marginBottom: 14, textAlign: 'center', color: 'var(--ink-2)' }}>
          No statutory rules yet. <strong>Nothing is pre-filled on purpose</strong> — every rate must come from your accountant.
        </div>
      )}
      {unverified > 0 && (
        <div className="pill pill-warn" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
          {unverified} rule(s) awaiting verification — payroll can't be finalized until they're signed off.
        </div>
      )}

      <div className="card" style={{ overflowX: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
            <Th>Rule</Th><Th>Kind</Th><Th>Computation</Th><Th>Effective</Th><Th>Paid by</Th><Th>Verified</Th><Th></Th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                <Td><strong>{r.label}</strong></Td>
                <Td>{r.kind}</Td>
                <Td>{r.basis === 'PercentOfBase' ? `${(r.value / 100).toFixed(2)}% of base` : rupees(r.value)}</Td>
                <Td>{r.effectiveFrom}</Td>
                <Td>{r.isEmployerContribution ? 'Employer' : 'Employee'}</Td>
                <Td>
                  {r.isVerified
                    ? <span className="pill pill-ok" style={{ fontSize: 11 }}>✓ {r.verifiedBy}</span>
                    : <span className="pill pill-warn" style={{ fontSize: 11 }}>unverified</span>}
                </Td>
                <Td>{!r.isVerified && <button className="btn" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => verify(r)}>Verify</button>}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td colSpan={7} muted>—</Td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: 16, maxWidth: 640 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10 }}>Add a statutory rule</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><label className="label">Kind</label>
            <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="ProvidentFund">Provident Fund (PF)</option>
              <option value="Esi">ESI</option>
              <option value="ProfessionalTax">Professional Tax</option>
              <option value="Tds">TDS</option>
            </select>
          </div>
          <div><label className="label">Label</label><input className="input" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Employee PF" /></div>
          <div><label className="label">Basis</label>
            <select className="input" value={f.basis} onChange={(e) => setF({ ...f, basis: e.target.value })}>
              <option value="PercentOfBase">Percent of base</option>
              <option value="FlatAmount">Flat amount</option>
            </select>
          </div>
          <div><label className="label">{f.basis === 'PercentOfBase' ? 'Value (basis pts, 1200 = 12%)' : 'Amount (paise)'}</label>
            <input className="input" type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />
          </div>
          <div><label className="label">Wage floor (paise, 0 = none)</label><input className="input" type="number" value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })} /></div>
          <div><label className="label">Wage ceiling (paise, 0 = none)</label><input className="input" type="number" value={f.ceiling} onChange={(e) => setF({ ...f, ceiling: e.target.value })} /></div>
          <div><label className="label">Effective from</label><input className="input" type="date" value={f.effectiveFrom} onChange={(e) => setF({ ...f, effectiveFrom: e.target.value })} /></div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={f.employer} onChange={(e) => setF({ ...f, employer: e.target.checked })} /> Employer contribution
            </label>
          </div>
        </div>
        <div style={{ marginTop: 8 }}><label className="label">Source note (statute / who confirmed)</label><input className="input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
        <button className="btn btn-accent" style={{ marginTop: 12 }} disabled={!f.label.trim() || !f.effectiveFrom || !f.value} onClick={add}>Add rule (unverified)</button>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>New rules are always created unverified. Editing a verified rule's numbers revokes its verification.</p>
      </div>
    </div>
  );
}

function StructuresTab() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [structures, setStructures] = useState<SalaryStructure[]>([]);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(() => {
    hrApi.listEmployees().then((r) => setEmployees(asList(r))).catch(() => setEmployees([]));
    payrollApi.listStructures().then((r) => setStructures(asList(r))).catch(() => setStructures([]));
  }, []);
  useEffect(load, [load]);

  const structureFor = (id: string) => structures.find((s) => s.employeeId === id) ?? null;

  return (
    <div>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
            <Th>Employee</Th><Th>Gross</Th><Th>Statutory base</Th><Th></Th>
          </tr></thead>
          <tbody>
            {employees.length === 0 && <tr><Td colSpan={4} muted>No employees.</Td></tr>}
            {employees.map((e) => {
              const s = structureFor(e.id);
              return (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <Td><strong>{e.fullName}</strong> <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{e.employeeCode}</span></Td>
                  <Td>{s ? rupees(s.grossPaise) : <span style={{ color: 'var(--ink-3)' }}>not on payroll</span>}</Td>
                  <Td>{s ? rupees(s.statutoryBasePaise) : '—'}</Td>
                  <Td><button className="btn" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setEditing(e.id)}>{s ? 'Edit' : 'Set salary'}</button></Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <StructureDialog employeeId={editing} existing={structureFor(editing)} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function StructureDialog({ employeeId, existing, onClose, onDone }: { employeeId: string; existing: SalaryStructure | null; onClose: () => void; onDone: () => void }) {
  const [rows, setRows] = useState(existing?.components.map((c) => ({ ...c })) ?? [
    { label: 'Basic', kind: 'Earning', amountPaise: 0, countsForStatutoryBase: true },
  ]);
  const [effectiveFrom, setEffectiveFrom] = useState(existing?.effectiveFrom ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (i: number, patch: Partial<typeof rows[number]>) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const save = async () => {
    if (!effectiveFrom) return;
    setBusy(true); setError(null);
    try { await payrollApi.setStructure({ employeeId, effectiveFrom, components: rows.filter((r) => r.label.trim()) }); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 22, width: 560, maxWidth: '94vw', maxHeight: '86vh', overflowY: 'auto' }}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 10 }}>Salary structure</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, justifyContent: 'flex-start' }}>{error}</div>}
        <div style={{ marginBottom: 12 }}><label className="label">Effective from</label><input className="input" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} style={{ maxWidth: 200 }} /></div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Components</div>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <input className="input" value={r.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Basic / HRA" style={{ flex: 1 }} />
            <select className="input" value={r.kind} onChange={(e) => update(i, { kind: e.target.value })} style={{ width: 110 }}>
              <option value="Earning">Earning</option><option value="Deduction">Deduction</option>
            </select>
            <input className="input" type="number" value={r.amountPaise} onChange={(e) => update(i, { amountPaise: Number(e.target.value) })} placeholder="paise" style={{ width: 110 }} />
            <label title="Counts toward the base for percent-based statutory rules" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
              <input type="checkbox" checked={r.countsForStatutoryBase} onChange={(e) => update(i, { countsForStatutoryBase: e.target.checked })} /> base
            </label>
            <button className="btn" style={{ padding: '3px 8px' }} onClick={() => setRows(rows.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        ))}
        <button className="btn" onClick={() => setRows([...rows, { label: '', kind: 'Earning', amountPaise: 0, countsForStatutoryBase: false }])}>+ Add component</button>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>Tick "base" on the components that count toward percent-based statutory deductions (typically Basic + DA). <strong>You decide</strong> — the software never assumes.</p>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !effectiveFrom} onClick={save} style={{ marginLeft: 'auto' }}>{busy ? 'Saving…' : 'Save structure'}</button>
        </div>
      </div>
    </div>
  );
}

function RunsTab() {
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [active, setActive] = useState<PayrollRun | null>(null);

  /**
   * ⚠ ONE SHAPE TEST, USED BY ALL THREE PATHS THAT SET A RUN. `unverifiedRules` is the field the
   * panel reads first, so it is the field worth asserting — a response missing it renders a stack
   * trace instead of a payroll, and there are three separate ways to get one.
   */
  const isRun = (r: unknown): r is PayrollRun =>
    !!r && Array.isArray((r as { unverifiedRules?: unknown[] }).unverifiedRules);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [ym, setYm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    payrollApi.listRuns().then((r) => setRuns(asList(r))).catch(() => setRuns([]));
    hrApi.listEmployees().then((r) => setEmployees(asList(r))).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const name = (id: string) => employees.find((e) => e.id === id)?.fullName ?? '—';

  const compute = async () => {
    setBusy(true); setError(null);
    /**
     * ⚠ THE RUN IS CHECKED FOR SHAPE. `{}` from a failed compute is truthy, so the panel rendered
     * and `active.unverifiedRules.length` threw — on the screen that produces salaries. The clerk
     * sees a stack trace where a draft payroll should be, and has no way to tell whether anything
     * was computed.
     */
    try { const r = await payrollApi.compute(ym.year, ym.month); setActive(isRun(r) ? r : null); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not compute.'); }
    finally { setBusy(false); }
  };

  const finalize = async () => {
    if (!active) return;
    setBusy(true); setError(null);
    try { const r = await payrollApi.finalize(active.id); setActive(isRun(r) ? r : null); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not finalize.'); }
    finally { setBusy(false); }
  };

  /**
   * Payslip columns.
   *
   * ⚠ Inside the component: `name()` resolves an employee id through state, and a module-scope
   * definition could not reach it.
   */
  const payslipColumns: Column<NonNullable<typeof active>['payslips'][number]>[] = [
    {
      id: 'employee', header: 'Employee', sortable: true, primary: true,
      value: (p) => name(p.employeeId),
      cell: (p) => name(p.employeeId),
    },
    {
      id: 'gross', header: 'Gross', align: 'right', sortable: true,
      // Paise, not the rendered string — sorting "₹9,000" above "₹80,000" is the bug a payroll
      // clerk spots in the first minute.
      value: (p) => p.grossPaise,
      cell: (p) => rupees(p.grossPaise),
    },
    {
      id: 'deductions', header: 'Deductions', align: 'right', sortable: true,
      // ⚠ SUMMED DEFENSIVELY. NaN + 5 is NaN, so one absent field poisons the sort order as well
      //   as the display, and a column sorted by NaN puts rows in an arbitrary place silently.
      value: (p) => (p.employeeDeductionsPaise ?? 0) + (p.voluntaryDeductionsPaise ?? 0),
      cell: (p) => (
        // The breakdown stays on hover — a clerk questioning a number wants to see what made it up
        // without opening anything.
        <span title={p.lines.filter((l) => !l.isEmployerContribution)
          .map((l) => `${l.label}: ${rupees(l.amountPaise)}`).join(', ')}>
          {p.employeeDeductionsPaise === undefined && p.voluntaryDeductionsPaise === undefined
            ? '—'
            : rupees((p.employeeDeductionsPaise ?? 0) + (p.voluntaryDeductionsPaise ?? 0))}
        </span>
      ),
    },
    {
      id: 'net', header: 'Net', align: 'right', sortable: true,
      value: (p) => p.netPaise,
      cell: (p) => <strong>{rupees(p.netPaise)}</strong>,
    },
  ];

  return (
    <div>
      {error && <div className="pill pill-danger" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start', display: 'block' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' }}>
        <div><label className="label">Year</label><input className="input" type="number" value={ym.year} onChange={(e) => setYm({ ...ym, year: Number(e.target.value) })} style={{ width: 100 }} /></div>
        <div><label className="label">Month</label><input className="input" type="number" min={1} max={12} value={ym.month} onChange={(e) => setYm({ ...ym, month: Number(e.target.value) })} style={{ width: 80 }} /></div>
        <button className="btn btn-accent" disabled={busy} onClick={compute}>Compute draft</button>
      </div>

      {active && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <h3 style={{ fontSize: 16 }}>{active.month}/{active.year}</h3>
            <span className={`pill ${active.status === 'Finalized' ? 'pill-ok' : 'pill-muted'}`} style={{ fontSize: 11 }}>{active.status}</span>
            <strong style={{ marginLeft: 'auto' }}>{rupees(active.totalNetPaise)}</strong>
          </div>

          {active.unverifiedRules.length > 0 && (
            <div className="pill pill-warn" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, display: 'block' }}>
              ⚠ Cannot finalize — these statutory rules are unverified: <strong>{active.unverifiedRules.join(', ')}</strong>.
              Have your accountant verify them on the Statutory rules tab.
            </div>
          )}

          <DataTable
            rows={active.payslips}
            columns={payslipColumns}
            rowKey={(p) => p.employeeId}

            search
            searchPlaceholder="Search an employee…"

            // ⚠ No paging. A payroll run is CHECKED, not browsed — a clerk reads every line before
            // finalising, and paging it means finalising a run they have only partly seen.
            pageSize={500}

            empty={{
              title: 'No payslips',
              body: 'No employees have a salary structure yet.',
            }}
          />

          {active.status === 'Draft' && (
            <button className="btn btn-accent" style={{ marginTop: 14 }} disabled={busy || active.unverifiedRules.length > 0} onClick={finalize}>
              Finalize &amp; issue payslips
            </button>
          )}

          {active.status === 'Finalized' && <BankFile runId={active.id} />}
        </div>
      )}

      <LopDivisorPicker />

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>Past runs</div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {runs.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>No runs yet.</div> :
          runs.map((r) => (
            <button key={r.id} onClick={async () => { const got = await payrollApi.getRun(r.id); setActive(isRun(got) ? got : null); }}
              style={{ display: 'flex', width: '100%', gap: 10, alignItems: 'center', padding: '10px 14px', background: 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer', textAlign: 'left' }}>
              <strong>{r.month}/{r.year}</strong>
              <span className={`pill ${r.status === 'Finalized' ? 'pill-ok' : 'pill-muted'}`} style={{ fontSize: 11 }}>{r.status}</span>
              <span style={{ marginLeft: 'auto' }}>{rupees(r.totalNetPaise)}</span>
            </button>
          ))}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14, color: active ? 'var(--ink)' : 'var(--ink-3)', fontWeight: active ? 600 : 400, borderBottom: active ? '2px solid var(--accent,#C1272D)' : '2px solid transparent' }}>{children}</button>;
}
function Th({ children }: { children?: ReactNode }) {
  return <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' }}>{children}</th>;
}
function Td({ children, colSpan, muted, title }: { children?: ReactNode; colSpan?: number; muted?: boolean; title?: string }) {
  return <td colSpan={colSpan} title={title} style={{ padding: '10px 14px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : 'left', fontSize: 13 }}>{children}</td>;
}


/**
 * ═══ THE BANK FILE ═══
 *
 * ⚠ THE SCHOOL'S BANK MOVES THE MONEY; THIS PREPARES IT. Eduvora never holds or transfers a rupee —
 * pooling somebody else's funds is RBI payment-aggregator territory, and no chairman is going to
 * hand a software vendor the ability to move twenty lakh out of the school account anyway. What he
 * actually wants is his clerk not re-typing forty rows into net banking every month.
 *
 * ⚠ THE PROBLEM LIST COMES FIRST AND THE DOWNLOAD IS ONLY OFFERED AFTER IT IS EMPTY. One bad IFSC
 * discovered by the bank costs a day and one person's salary; the same fact on this screen costs a
 * minute. And the file is all-or-nothing on purpose — thirty-nine of forty rows uploads cleanly,
 * pays thirty-nine people, and the fortieth finds out by not being paid.
 */
function BankFile({ runId }: { runId: string }) {
  const [p, setP] = useState<PaymentFilePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [debitAccount, setDebitAccount] = useState('');
  const [format, setFormat] = useState('Generic');
  const [rail, setRail] = useState('Neft');
  const [valueDate, setValueDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    payrollApi.paymentFilePreview(runId).then(setP)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not check the bank details.'));
  }, [runId]);

  if (err) return <p style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 14 }}>{err}</p>;
  if (!p) return null;

  return (
    <div style={{ borderTop: '1px solid var(--line)', marginTop: 16, paddingTop: 14 }}>
      <div style={{ fontWeight: 650, fontSize: 13.5, marginBottom: 4 }}>Pay this run</div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 10px', maxWidth: 620 }}>
        This makes the file your bank's net banking accepts. Your accounts staff upload it and
        authorise the payment — Eduvora never touches the money.
      </p>

      {p.problems.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {/* ⚠ Named, with the fault in words. "Row 14 invalid" sends somebody counting rows. */}
          <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>Cannot be paid yet</div>
          {p.problems.map((x) => (
            <div key={x.employeeId} style={{ fontSize: 12.5, marginBottom: 2 }}>
              <strong>{x.fullName || 'Unnamed employee'}</strong>
              <span style={{ color: 'var(--ink-2)' }}> — {x.what}</span>
            </div>
          ))}
        </div>
      )}

      {p.refusal ? (
        <p style={{ fontSize: 12.5, color: 'var(--warn)', margin: '0 0 10px', maxWidth: 620 }}>{p.refusal}</p>
      ) : (
        <>
          <p style={{ fontSize: 13, margin: '0 0 10px' }}>
            <strong>{p.payable}</strong> {p.payable === 1 ? 'person' : 'people'} · {rupees(p.totalPaise)} in total
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label className="label">School's account (debited)</label>
              <input className="input" value={debitAccount} placeholder="Account number"
                     style={{ width: 190 }} onChange={(e) => setDebitAccount(e.target.value)} />
            </div>
            <div>
              <label className="label">Bank</label>
              <select className="input" value={format} style={{ width: 'auto' }}
                      onChange={(e) => setFormat(e.target.value)}>
                {/* ⚠ Generic first and default: every portal takes a plain labelled sheet with one
                    mapping, and guessing a layout wrongly fails on upload. */}
                <option value="Generic">Any (plain columns)</option>
                <option value="Sbi">SBI</option>
                <option value="Hdfc">HDFC</option>
                <option value="Icici">ICICI</option>
                <option value="Axis">Axis</option>
              </select>
            </div>
            <div>
              <label className="label">Send by</label>
              <select className="input" value={rail} style={{ width: 'auto' }}
                      onChange={(e) => setRail(e.target.value)}>
                <option value="Neft">NEFT</option>
                <option value="Imps">IMPS</option>
                <option value="Rtgs">RTGS</option>
              </select>
            </div>
            <div>
              <label className="label">Value date</label>
              <input className="input" type="date" value={valueDate} style={{ width: 'auto' }}
                     onChange={(e) => setValueDate(e.target.value)} />
            </div>
            {/* ⚠ A plain anchor, not a fetch. The browser honours the filename the server sets and
                nothing re-encodes the CSV on the way through. */}
            <a className={`btn btn-accent ${debitAccount.trim() ? '' : 'is-disabled'}`}
               href={debitAccount.trim()
                 ? payrollApi.paymentFileUrl(runId, { format, rail, debitAccount: debitAccount.trim(), valueDate })
                 : undefined}
               aria-disabled={!debitAccount.trim()}
               style={{ pointerEvents: debitAccount.trim() ? undefined : 'none',
                        opacity: debitAccount.trim() ? 1 : 0.5, textDecoration: 'none' }}>
              Download the bank file
            </a>
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '8px 0 0', maxWidth: 620 }}>
            {/* ⚠ Said on the screen, because it is the boundary of what this software does. */}
            PF, ESI, professional tax and TDS are computed and on the payslips, but depositing them
            stays with the school.
          </p>
        </>
      )}
    </div>
  );
}


/**
 * ═══ WHAT A DAY OFF COSTS ═══
 *
 * ⚠ IT ASKS UNTIL SOMEBODY ANSWERS. The same single absence costs ₹1,000, ₹1,071 or ₹1,250 on a
 * ₹30,000 salary depending purely on this, and all three are defensible. A screen that simply showed
 * "30 days" would present a fallback as a decision — and the school would discover it never made one
 * the month a teacher disputes a deduction.
 */
function LopDivisorPicker() {
  const [s, setS] = useState<LopSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    payrollApi.lopSettings().then(setS)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not read the setting.'));
  }, []);

  const choose = async (divisor: string) => {
    setBusy(true); setErr(null);
    try { setS(await payrollApi.setLopDivisor(divisor)); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save it.'); }
    finally { setBusy(false); }
  };

  if (!s) return null;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 650, fontSize: 13.5, marginBottom: 2 }}>Unpaid leave and absence</div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 10px', maxWidth: 640 }}>
        A day not worked is deducted from that month's salary. How much a day is worth depends on what
        you divide the salary by — and that is your school's policy, not a rule we can pick for you.
      </p>

      {/* ⚠ The nudge is the feature. Without it a fallback looks like a decision. */}
      {!s.chosen && (
        <p style={{ fontSize: 12.5, color: 'var(--warn)', margin: '0 0 10px', maxWidth: 640 }}>
          Nobody has chosen yet, so payroll is using 30 days. That is the commonest answer here, but
          please confirm it — a teacher will one day ask how a deduction was worked out.
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {s.options.map((o) => (
          <button key={o.value} className="btn" disabled={busy} onClick={() => void choose(o.value)}
            style={{ background: s.divisor === o.value ? 'var(--brand)' : undefined,
                     color: s.divisor === o.value ? '#fff' : undefined }}>
            {o.label}
          </button>
        ))}
      </div>

      {/* ⚠ The arithmetic, in rupees, on the screen where the choice is made. "Working days" means
          nothing until you see that it is ₹250 a day dearer than thirty days. */}
      <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '10px 0 0' }}>
        On a ₹30,000 salary in February, one day off costs ₹1,000 on 30 days, ₹1,071 on the days in
        the month, or ₹1,250 if the school opened 24 days.
      </p>

      {err && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '8px 0 0' }}>{err}</p>}
    </div>
  );
}
